<template>
  <div class="page-container">
    <div v-if="loading" class="loading-state text-tertiary">Loading...</div>

    <template v-else-if="post">
      <router-link to="/blog" class="back-link">&larr; {{ $t('back_to_blog') }}</router-link>

      <article class="post-article">
        <h1 class="post-title">{{ post.title }}</h1>
        <div class="post-meta text-tertiary text-sm mb-3">
          <span>{{ formatDate(post.created_at) }}</span>
        </div>
        <div class="post-content markdown-body" v-html="renderedContent" />
        <div v-if="post.allow_likes" class="post-like-section">
          <button class="like-btn" :class="{ liked }" @click="handleLike">
            <svg width="18" height="18" viewBox="0 0 24 24" :fill="liked ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.5">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
            </svg>
            <span>{{ post.like_count || 0 }}</span>
          </button>
        </div>
      </article>

      <div class="post-divider" />

      <CommentSection :postId="postId" />
    </template>

    <div v-else class="empty-state">
      <p class="text-tertiary">{{ $t('not_found') }}</p>
    </div>
  </div>
</template>

<script>
import { getPost, toggleLike } from '../api'
import CommentSection from '../components/CommentSection.vue'
import { marked } from 'marked'
import hljs from 'highlight.js'
import { addCopyButtons } from '../utils/code-copy'

marked.setOptions({
  breaks: true,
  highlight: function(code, lang) {
    if (lang && hljs.getLanguage(lang)) {
      return hljs.highlight(code, { language: lang }).value
    }
    return hljs.highlightAuto(code).value
  }
})

export default {
  name: 'BlogPost',
  components: { CommentSection },
  data() {
    return {
      post: null,
      liked: false,
      loading: true
    }
  },
  computed: {
    postId() {
      return Number(this.$route.params.id)
    },
    renderedContent() {
      if (!this.post || !this.post.content) return ''
      return marked(this.post.content)
    }
  },
  methods: {
    formatDate(d) {
      if (!d) return ''
      const date = new Date(d)
      return date.toLocaleDateString(this.$i18nState.lang === 'en' ? 'en-US' : 'zh-CN', {
        year: 'numeric', month: 'long', day: 'numeric'
      })
    },
    fetchPost() {
      this.loading = true
      getPost(this.postId).then(res => {
        const d = res.data.data
        this.post = d.post
        this.liked = d.liked
      }).catch(() => {
        this.post = null
      }).finally(() => {
        this.loading = false
      })
    },
    handleLike() {
      toggleLike(this.postId).then(res => {
        this.liked = res.data.data.liked
        this.post.like_count = res.data.data.like_count
      })
    }
  },
  watch: {
    post() {
      this.$nextTick(() => {
        setTimeout(() => {
          addCopyButtons(this.$el.querySelector('.post-content'))
        }, 100)
      })
    }
  },
  mounted() {
    this.fetchPost()
  }
}
</script>

<style scoped>
.back-link {
  display: inline-block;
  font-size: 0.85rem;
  color: var(--text-tertiary);
  margin-bottom: 24px;
  transition: color 0.2s ease;
}
.back-link:hover {
  color: var(--text-primary);
}
.post-article {
  margin-bottom: 48px;
}
.post-title {
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.35;
}
.post-content {
  font-size: 1rem;
  line-height: 1.85;
}
.post-content :deep(img) {
  border-radius: 6px;
  margin: 16px 0;
}
.post-content :deep(blockquote) {
  border-left: 2px solid var(--border-color);
  padding-left: 16px;
  color: var(--text-secondary);
  margin: 16px 0;
}
.post-content :deep(h1), .post-content :deep(h2), .post-content :deep(h3) {
  margin-top: 28px;
  margin-bottom: 12px;
  font-weight: 600;
}
.post-content :deep(p) {
  margin-bottom: 1em;
}
.post-content :deep(a) {
  color: var(--text-secondary);
  text-decoration: underline;
}
.post-content :deep(pre) {
  margin: 16px 0;
  border-radius: 8px;
  overflow: hidden;
}
.post-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
}
.post-content :deep(th), .post-content :deep(td) {
  border: 1px solid var(--border-color);
  padding: 8px 12px;
  text-align: left;
}
.post-like-section { margin-top: 24px; display: flex; align-items: center; }
.like-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border: 1px solid var(--border-color); border-radius: 20px; background: var(--bg-primary); color: var(--text-secondary); font-size: 0.85rem; font-family: inherit; cursor: pointer; transition: all 0.15s ease; }
.like-btn:hover { background: var(--hover-bg); color: var(--text-primary); }
.like-btn.liked { color: var(--text-primary); }
.post-divider {
  height: 1px;
  background: var(--border-light);
  margin: 48px 0;
}
.loading-state, .empty-state {
  padding: 80px 0;
  text-align: center;
}
</style>
